/**
 * The product browser — the canonical things, not the per-catalog identities.
 *
 * A row here may be several identities from several databases fused into one
 * product; `identityCount > 1` is the visible sign of that. Select two or more
 * and press Fuse to say "these are the same thing"; the survivor is the first one
 * selected.
 */
import { useState } from 'react';
import { useApi } from '../lib/api';
import { useList } from '../lib/hooks';
import { useNav } from '../lib/router';
import type { ProductRow } from '../lib/types';
import { qs } from '../lib/util';
import {
  Banner,
  Chip,
  DataBlock,
  Empty,
  ErrorBox,
  Loading,
  Pager,
  Seg,
  SourceChip,
  Table,
  Toolbar,
} from '../ui';

const LIMIT = 50;

export default function Products() {
  const { request } = useApi();
  const { openProduct } = useNav();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<'' | 'observed' | 'internal'>('');
  const [source, setSource] = useState('');
  const [fused, setFused] = useState(false);
  const [offset, setOffset] = useState(0);
  const [picked, setPicked] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const path = `/v1/products${qs({ q, kind, source, fused: fused ? '1' : '', limit: LIMIT, offset })}`;
  const { data, loading, error, reload } = useList<ProductRow>(path);

  const reset = (fn: () => void) => {
    fn();
    setOffset(0);
    setPicked([]);
  };

  const toggle = (id: number) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  async function fuse() {
    if (picked.length < 2) return;
    const [into] = picked;
    if (
      !window.confirm(
        `Fuse ${picked.length} products into #${into}?\n\nThis says they are the same physical part. It is reversible: open the product and split an identity back out.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setErr('');
    const r = await request('POST', '/v1/admin/products/fuse', { ids: picked, into, reason: 'console' });
    setBusy(false);
    if (r.status >= 400) {
      setErr(String((r.data as { error?: string })?.error ?? `HTTP ${r.status}`));
      return;
    }
    setPicked([]);
    reload();
  }

  const items = data?.items ?? [];

  return (
    <>
      <Banner icon="◎">
        One row per <b>product</b> — the physical part — not per catalog listing. A product with more than one identity
        has been fused from several codes, possibly across databases.
      </Banner>
      <Toolbar>
        <input
          className="input mono"
          style={{ width: 220 }}
          placeholder="Brand or article…"
          value={q}
          onChange={(e) => reset(() => setQ(e.target.value))}
        />
        <Seg
          value={kind}
          onChange={(v) => reset(() => setKind(v))}
          options={[
            { value: '', label: 'All' },
            { value: 'observed', label: 'Observed' },
            { value: 'internal', label: 'Internal' },
          ]}
        />
        <select
          className="input"
          style={{ width: 'auto' }}
          value={source}
          onChange={(e) => reset(() => setSource(e.target.value))}
        >
          <option value="">Any source</option>
          <option value="kromeda">Kromeda</option>
          <option value="tecdoc">TecDoc</option>
          <option value="internal">Internal</option>
        </select>
        <label className="flex cursor-pointer items-center gap-1.5 text-[12.5px] text-muted">
          <input type="checkbox" checked={fused} onChange={(e) => reset(() => setFused(e.target.checked))} />
          Fused only
        </label>
        <div className="flex-1" />
        {picked.length > 0 && (
          <>
            <span className="text-[12.5px] text-muted">{picked.length} selected</span>
            <button className="btn btn-sm" disabled={picked.length < 2 || busy} onClick={fuse}>
              Fuse into #{picked[0]}
            </button>
            <button className="btn btn-sm" onClick={() => setPicked([])}>
              Clear
            </button>
          </>
        )}
      </Toolbar>
      {(error || err) && <ErrorBox>{err || error}</ErrorBox>}
      <DataBlock
        title="Products"
        sub={data ? `${data.total.toLocaleString()} products` : undefined}
        raw={data}
        right={data ? <Pager total={data.total} limit={LIMIT} offset={offset} onOffset={setOffset} /> : undefined}
      >
        {loading ? (
          <Loading />
        ) : items.length === 0 ? (
          <Empty>No products yet — run a search, or import an internal one.</Empty>
        ) : (
          <Table cols={['', 'Brand', 'Article', 'Name', 'Identities', 'OEs', 'Cross', 'Seen in', '']}>
            {items.map((p) => (
              <tr key={p.id} className={picked.includes(p.id) ? 'bg-accentDim' : undefined}>
                <td>
                  <input type="checkbox" checked={picked.includes(p.id)} onChange={() => toggle(p.id)} />
                </td>
                <td className="mono">{p.brandNorm}</td>
                <td className="mono">{p.articleNorm}</td>
                <td>
                  {p.name ?? '—'}
                  {p.kind === 'internal' && <Chip className="ml-1.5 border-accent text-accent">internal</Chip>}
                </td>
                <td className="num">
                  {p.identityCount > 1 ? <b className="text-accent">{p.identityCount}</b> : p.identityCount}
                </td>
                <td className="num">{p.oeCount}</td>
                <td className="num">{p.crossCount}</td>
                <td>
                  <div className="flex gap-1">
                    {p.sources.map((s) => (
                      <SourceChip key={s} source={s} />
                    ))}
                  </div>
                </td>
                <td>
                  <button className="btn btn-sm" onClick={() => openProduct(p.id)}>
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </DataBlock>
      <div className="mt-1 text-[11.5px] text-faint">
        Fusing is reversible — nothing is rewritten, only the membership of an identity moves. Open a product to split
        one back out.
      </div>
    </>
  );
}
